// LiveRack.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './LiveRack.module.scss';
import classNames from 'classnames';
import { locales, VelocityRate, AutomaticMetricsType } from '../../constants';
import { getInitialsFromFirstAndLastName } from '../../utils';
import { LiveRepFrame } from '../../types/automatic';
import Tooltip from '@mui/material/Tooltip';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';

import { useSessionData } from '../../providers';

interface LiveRackProps {
  number: number;
  data: Record<string, LiveRepFrame[]>;
  activeUserId: string | null;
  status?: string;
  rtServiceOnline?: boolean;
  currentMemberId?: string | null;
  onClick?: (rackNumber: number) => void;
}

const texts = locales.en.components.liveWorkouts.liveRack;

function classifyByType(
  value: number | undefined,
  min: number | undefined,
  max: number | undefined,
  type?: AutomaticMetricsType,
): 'good' | 'fast' | 'slow' {
  if (value == null || min == null || max == null) return 'good';
  if (type === AutomaticMetricsType.Velocity) {
    // Velocity: below min => too slow, above max => too fast
    if (value < min) return 'slow';
    if (value > max) return 'fast';
    return 'good';
  }
  // Tempo default: below min => too fast, above max => too slow
  if (value < min) return 'fast';
  if (value > max) return 'slow';
  return 'good';
}

const LiveRack: React.FC<LiveRackProps> = ({
  number,
  data,
  activeUserId,
  status,
  rtServiceOnline,
  currentMemberId,
  onClick,
}) => {
  const [overallVelocityRate, setOverallVelocityRate] = useState<VelocityRate>(
    VelocityRate.Good,
  );

  const userIds = useMemo(() => Object.keys(data), [data]);

  const userClassifications = useMemo(() => {
    return userIds.map((userId) => {
      const metrics = data[userId] || [];
      if (metrics.length === 0) {
        return {
          userId,
          isActiveLive: userId === activeUserId,
          color: 'neutral',
          goodCount: 0,
          fastCount: 0,
          slowCount: 0,
          totalCount: 0,
        };
      }

      const lastFrame = metrics[metrics.length - 1];
      const { minConcentric, maxConcentric, concentricType } =
        lastFrame.preset || {};

      let goodCount = 0;
      let fastCount = 0;
      let slowCount = 0;

      if (minConcentric == null || maxConcentric == null) {
        // No preset info, consider all as good reps
        goodCount = metrics.length;
      } else {
        for (const frame of metrics) {
          const value = frame.upDuration;
          const repType = classifyByType(
            value,
            minConcentric,
            maxConcentric,
            concentricType,
          );
          if (repType === 'good') goodCount++;
          if (repType === 'fast') fastCount++;
          if (repType === 'slow') slowCount++;
        }
      }

      const totalCount = goodCount + fastCount + slowCount;
      let color = 'green';
      if (totalCount > 0) {
        const goodPct = goodCount / totalCount;
        const fastPct = fastCount / totalCount;
        const slowPct = slowCount / totalCount;
        if (goodPct > fastPct && goodPct > slowPct) {
          color = 'green';
        } else if (fastPct > goodPct && fastPct > slowPct) {
          color = 'yellow';
        } else if (slowPct > goodPct && slowPct > fastPct) {
          color = 'red';
        }
      }

      return {
        userId,
        isActiveLive: userId === activeUserId,
        color,
        goodCount,
        fastCount,
        slowCount,
        totalCount,
      };
    });
  }, [data, userIds, activeUserId]);

  useEffect(() => {
    let totalGood = 0;
    let totalFast = 0;
    let totalSlow = 0;

    for (const u of userClassifications) {
      totalGood += u.goodCount;
      totalFast += u.fastCount;
      totalSlow += u.slowCount;
    }

    const totalReps = totalGood + totalFast + totalSlow;
    let overall = VelocityRate.Good;

    if (totalReps > 0) {
      const goodPct = totalGood / totalReps;
      const fastPct = totalFast / totalReps;
      const slowPct = totalSlow / totalReps;
      if (goodPct > fastPct && goodPct > slowPct) overall = VelocityRate.Good;
      else if (fastPct > goodPct && fastPct > slowPct)
        overall = VelocityRate.Fast;
      else if (slowPct > goodPct && slowPct > fastPct)
        overall = VelocityRate.Slow;
    }

    setOverallVelocityRate(overall);
  }, [userClassifications]);

  const { members, mergedExercisesMap } = useSessionData();
  const getMemberInitials = (userId: string) => {
    const member = members[userId];
    if (!member) return '';
    const first = member.firstName ?? member.name?.split(' ')?.[0] ?? '';
    const last =
      member.lastName ??
      (member.name && member.name.split(' ').length > 1
        ? member.name.split(' ')[1]
        : '');
    return getInitialsFromFirstAndLastName(first, last);
  };

  const handleClick = useCallback(() => {
    if (onClick) onClick(number);
  }, [onClick, number]);

  const getUserTooltip = (userId: string) => {
    const frames = data[userId] || [];
    const latestFrame = frames[frames.length - 1];
    if (!latestFrame) {
      const member = members[userId];
      return (
        <div className={styles.userTooltip}>
          <div className={styles.userTooltipTitle}>
            {member?.name || 'No Active Set'}
          </div>
          <div className={styles.userTooltipRow}>
            <span className={styles.userTooltipLabel}>Reps Completed:</span>
            <span>0/0</span>
          </div>
        </div>
      );
    }

    const exerciseName =
      (latestFrame.exerciseId &&
        mergedExercisesMap?.[latestFrame.exerciseId]?.name) ||
      'Exercise';
    const weight = latestFrame.preset?.weight != null
      ? Math.round(latestFrame.preset.weight)
      : undefined;
    const completedReps = frames.length;
    const targetReps = latestFrame.preset?.reps ?? completedReps;

    return (
      <div className={styles.userTooltip}>
        <div className={styles.userTooltipTitle}>
          {exerciseName?.toUpperCase()}
        </div>
        <div className={styles.userTooltipRow}>
          <span className={styles.userTooltipLabel}>Weight:</span>
          <span>{weight != null ? `${weight} lbs` : '—'}</span>
        </div>
        <div className={styles.userTooltipRow}>
          <span className={styles.userTooltipLabel}>Reps Completed:</span>
          <span>
            {latestFrame.preset?.isAmrap
              ? `${completedReps} (AMRAP)`
              : `${completedReps}/${targetReps}`}
          </span>
        </div>
      </div>
    );
  };

  const renderStatusIcon = () => {
    if (status === 'CAMERA_ISSUE') {
      return (
        <Tooltip title="Camera Issue">
          <VideocamOffOutlinedIcon
            sx={{ color: 'var(--color-danger)', fontSize: 18 }}
          />
        </Tooltip>
      );
    }
    if (rtServiceOnline === false) {
      return (
        <Tooltip title="Service Offline">
          <PowerSettingsNewOutlinedIcon
            sx={{ color: 'var(--color-danger)', fontSize: 18 }}
          />
        </Tooltip>
      );
    }
    if (status === 'DOWN') {
      return (
        <Tooltip title="Service Down">
          <ReportProblemOutlinedIcon
            sx={{ color: 'var(--color-danger)', fontSize: 18 }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.goodRate]: overallVelocityRate === VelocityRate.Good,
        [styles.fastRate]: overallVelocityRate === VelocityRate.Fast,
        [styles.slowRate]: overallVelocityRate === VelocityRate.Slow,
      })}
      onClick={handleClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      <div className={styles.titleRow}>
        <span className={styles.title}>{`${texts.rack} ${number}`}</span>
        {renderStatusIcon()}
      </div>
      <div className={styles.usersContainer}>
        {userClassifications.map((u) => (
          <div
            key={u.userId}
            className={styles.userWrapper}
            style={{ width: `${100 / userIds.length}%` }}
          >
            <Tooltip
              enterTouchDelay={0}
              title={getUserTooltip(u.userId)}
              arrow
              placement="top"
            >
              <div
                className={classNames(styles.userBox, {
                  [styles.active]: u.isActiveLive,
                  [styles.goodRate]: u.totalCount > 0 && u.color === 'green',
                  [styles.fastRate]: u.totalCount > 0 && u.color === 'yellow',
                  [styles.slowRate]: u.totalCount > 0 && u.color === 'red',
                  [styles.neutralRate]: u.totalCount === 0,
                  [styles.opacityFull]: u.isActiveLive,
                  [styles.opacityMid]:
                    !activeUserId &&
                    currentMemberId === u.userId &&
                    !u.isActiveLive,
                  [styles.opacityDim]:
                    (!activeUserId && currentMemberId !== u.userId) ||
                    (activeUserId && !u.isActiveLive),
                })}
              />
            </Tooltip>
            <span
              className={classNames(styles.userInitials, {
                [styles.active]: u.isActiveLive,
              })}
            >
              {getMemberInitials(u.userId)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LiveRack;
